import { api } from '@marine/api-client/api';

/**
 * Phase 39.3 — Platform AI usage API client (CTRL-15).
 *
 * Typed wrapper around `GET /api/ai/usage`. The backend returns a
 * `{data: ...}` envelope which `api.getOne` unwraps automatically. Do NOT add
 * `select: (r) => r.data` on the consuming TanStack Query hook — the envelope
 * is already stripped here (memory rule `feedback_api_envelope_unwrap.md`).
 *
 * Response shape matches D-11 from the 39.3 context:
 *   - `window`: { from, to, days }
 *   - `providers` OR `features` (depends on `breakdown` param)
 *   - `openrouter_balance`: the live 5-min-cached OR key usage (null on failure)
 */

export interface AiUsageWindow {
  from: string; // 'YYYY-MM-DD'
  to: string; // 'YYYY-MM-DD'
  days: number;
}

export interface AiUsageProviderBucket {
  provider: string;
  total_tokens_in: number;
  total_tokens_out: number;
  total_requests: number;
  cost_estimate_usd: number;
  last_used_at: string | null;
}

export interface AiUsageFeatureBucket {
  feature: string;
  total_tokens_in: number;
  total_tokens_out: number;
  total_requests: number;
  cost_estimate_usd: number;
  last_used_at: string | null;
}

export interface AiUsageBalance {
  credits_remaining_usd: number | null; // null = unlimited OR key
  usage_today_usd: number | null;
  last_fetched_at: string;
  stale: boolean;
}

export interface AiUsageResponse {
  window: AiUsageWindow;
  providers?: AiUsageProviderBucket[];
  features?: AiUsageFeatureBucket[];
  openrouter_balance: AiUsageBalance | null;
}

export interface AiUsageQuery {
  from?: string;
  to?: string;
  breakdown?: 'provider' | 'feature';
}

export interface AiUsageRefreshResponse {
  openrouter_balance: AiUsageBalance | null;
}

export const aiUsageApi = {
  get: (params: AiUsageQuery = {}): Promise<AiUsageResponse> =>
    api.getOne<AiUsageResponse>('/api/ai/usage', { params }),
  // #647 — busts the server's 5-min key-usage cache and returns a balance
  // fetched with the current platform key.
  refresh: (): Promise<AiUsageRefreshResponse> =>
    api.postOne<AiUsageRefreshResponse>('/api/ai/usage/refresh'),
};
